import { yupResolver } from '@hookform/resolvers/yup';
import { DeleteTwoTone, EditTwoTone, InfoOutlined } from '@mui/icons-material';
import { Button, Dialog, Loader, Tooltip } from '@sortlist-frontend/design-system/legacy';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { Fragment, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ConfirmationDialog } from '_components/common/dialogs/ConfirmationDialog';
import { ExpertiseSelector } from '_components/form/ExpertiseSelector';
import { TextareaWrapped } from '_components/form/TextareaWrapped';
import { usePublicAppContext } from '_core/context/public-app-context';
import { Expertise, useExpertises } from '_core/repos/expertises.repo';
import { agencyClaimConfig } from '_features/agency/claim/agency-claim.config';
import { Service } from '_types/generic';

import { ForbiddenInfoWarning } from '../../ForbiddenInfoWarning';
import { Price } from './Price';
import { getSchema } from './utils';

type FormValuesType = {
  description?: string;
  budget?: number | null;
  expertise_id: string | number;
};

const getDefaultValues = (service: Service | undefined): FormValuesType => {
  return {
    description: service?.description || '',
    budget: service?.budget[0],
    expertise_id: service?.expertise_id || '',
  };
};

type ServiceFormProps = {
  service?: Service;
  opened?: boolean;
  onClose?: () => void;
  onSave: (service: Partial<Service>) => void;
  onDelete: (serviceId: number) => void;
  locale: string;
  selectedServices: Service[];
};

export const ServiceForm = (props: ServiceFormProps) => {
  const { selectedServices, service, opened, onClose, onSave, onDelete } = props;
  const { t } = useTranslation(agencyClaimConfig.i18nNamespaces);
  const { getUrl } = usePublicAppContext();
  const { data: expertises, isLoading } = useExpertises();
  const defaultValues = getDefaultValues(service);
  const termsUrl = getUrl('legal.terms');

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useForm({
    defaultValues,
    resolver: yupResolver(getSchema(t)),
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: true,
    shouldUnregister: true,
  });

  const { expertise_id } = watch();

  const currentExpertise = expertises?.find(
    (expertise) => expertise.expertiseId === (service?.expertise_id || expertise_id),
  );

  const [open, setOpen] = useState<boolean>(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(opened ? false : true);
  const [selectedService, setSelectedService] = useState<Expertise | undefined>(undefined);

  const handleDeleteOnClose = (serviceId: string | undefined) => {
    if (serviceId) {
      onDelete && onDelete(serviceId as unknown as number);
    }
    setOpen(false);
  };

  const onSubmit = (values: FormValuesType) => {
    const maxPosition =
      selectedServices.length > 0
        ? selectedServices.reduce((prev, current) => {
            return prev.position > current.position ? prev : current;
          }).position
        : 0;
    const serviceValues = {
      budget: [values.budget],
      description: values.description,
      skill_tags: [],
      expertise_id: values.expertise_id,
      position: maxPosition + 1,
    };
    onSave(serviceValues as unknown as Partial<Service>);
    setIsEditDialogOpen(true);
  };

  useEffect(() => {
    const { expertise_id } = getValues();
    setSelectedService(expertises?.find((expertise) => expertise.expertiseId === expertise_id));
  }, [getValues, expertises]);

  return (
    <div className={'px-2 width-100 overflow-hidden'} data-testid="service-form">
      <div data-testid="collapsed-true" className="width-100 layout-row layout-align-space-between-center">
        {!isLoading && <p className="medium">{currentExpertise?.name}</p>}
        {isLoading && <Loader size="xs" isPageLoader={false} />}
        <div className="layout-row layout-align-start-center">
          <Button
            fab
            onClick={() => {
              setIsEditDialogOpen(!isEditDialogOpen);
            }}
            size="xs"
            className="mr-2"
            animation="none"
            buttonVariant="light"
            buttonStyle="secondary"
            icon={<EditTwoTone style={{ fontSize: 16 }} />}
            data-testid="service-toggle-collapse"
            id="service-toggle-btn"
          />
          <Button
            fab
            onClick={() => {
              setOpen(true);
            }}
            size="xs"
            className="mr-2"
            animation="none"
            buttonVariant="light"
            buttonStyle="secondary"
            icon={<DeleteTwoTone style={{ fontSize: 16 }} />}
            data-testid="service-delete-service"
            id="delete-service-btn"
          />
        </div>
      </div>

      <ConfirmationDialog
        keepMounted
        open={open}
        onClose={handleDeleteOnClose}
        isLoading={false}
        value={service?.expertise_id as unknown as string}
        title={t('agency:claim.form.services.dialogs.title')}
        description={t('agency:claim.form.services.dialogs.description')}
        okLabel={t('agency:claim.form.services.dialogs.ok')}
        cancelLabel={t('common:general.cancel')}
      />

      <Dialog
        open={!isEditDialogOpen}
        handleClose={() => {
          setIsEditDialogOpen(!isEditDialogOpen);
          onClose && onClose();
        }}
        fullWidth
        maxWidth="md"
        headerClasses="p-4">
        <div className="px-8 py-4">
          <div data-testid="service-form-fields">
            <div className="pb-4">
              <Controller
                control={control}
                name="expertise_id"
                render={({ field: { onChange, value } }) => {
                  const filteredExpertises = expertises?.filter((exp) => {
                    if (service) {
                      const filteredServices = selectedServices?.filter((serv) => serv.expertise_id !== expertise_id);
                      return !filteredServices?.find((service) => service.expertise_id === exp.expertiseId);
                    } else {
                      return !selectedServices?.find((service) => service.expertise_id === exp.expertiseId);
                    }
                  });

                  return (
                    <Fragment>
                      <div className="layout-row layout-align-start-center">
                        <label className="p bold" htmlFor={'service'}>
                          {t('agency:claim.form.services.chooseService')}
                        </label>
                      </div>
                      <ExpertiseSelector
                        expertises={filteredExpertises}
                        selectedExpertiseId={expertise_id as number}
                        handleChange={(expertise) => {
                          onChange(expertise.expertiseId);
                          setValue('expertise_id', expertise.expertiseId);
                        }}
                      />
                    </Fragment>
                  );
                }}
              />
            </div>
            <div className="py-4">
              <Controller
                control={control}
                name="budget"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Fragment>
                      <div className="layout-row layout-align-start-center">
                        <label className="p bold" htmlFor={'description'}>
                          {t('agency:claim.form.services.startingPrice')}
                        </label>
                        <Tooltip placement="right" title={t('agency:claim.form.services.startingPriceTooltip')}>
                          <InfoOutlined className="ml-1 text-secondary-500" style={{ fontSize: 14 }} />
                        </Tooltip>
                      </div>
                      <Price price={value as number} onChange={onChange} error={errors?.budget?.message} />
                    </Fragment>
                  );
                }}
              />
            </div>
            <div className="py-4">
              <TextareaWrapped
                id="description"
                name="description"
                label={
                  <div className="layout-row layout-align-start-center">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="p" htmlFor={'description'}>
                      <Trans i18nKey={'agency:claim.form.services.descriptionOptional'}>
                        <span className="bold">Description </span>
                        <span className="small text-secondary-500">(optional)</span>
                      </Trans>
                    </label>
                    <ForbiddenInfoWarning />
                  </div>
                }
                placeholder={t('agency:claim.form.services.descriptionOptionalPlaceholder')}
                register={register}
                defaultValue={service?.description}
              />
            </div>
            <div className="layout-row py-4 layout-align-end-center">
              <Button
                size="md"
                buttonStyle="secondary"
                buttonVariant="light"
                className="mr-4"
                onClick={() => {
                  setIsEditDialogOpen(!isEditDialogOpen);
                  onClose && onClose();
                }}
                label={t('common:general.cancel')}
              />
              <Button
                label={t('common:general.save')}
                size="md"
                buttonStyle="primary"
                buttonVariant="raised"
                onClick={() => {
                  handleSubmit(onSubmit)();
                }}
                id="save-service-btn"
              />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
